import React from "react";

import { get, partial } from "lodash";

import { Text } from "@zapier/design-system";

import { Currency } from "@zapier/currency";
import BankAccountForm from "@zapier/payment/esm/components/BankAccountForm";
import PaymentInfo from "@zapier/payment/esm/components/PaymentInfo";
import {
  ApiKeys,
  BankPaymentSource,
  OverdueInvoice,
  PaymentDetailsBank,
  PaymentProcessor,
  PaymentSourceInput,
  PaymentSourceType,
  SupportedPaymentMethod,
} from "@zapier/payment/esm/types";

import PaymentMethod from "../PaymentMethod";
import { Aside } from "../shared-ui";

type Props = {
  /** API keys */
  apiKeys: ApiKeys;
  /** Currently select method */
  currentMethod: PaymentSourceType | null;
  /** Error message to show */
  error?: string | null;
  /** Hide switch component button */
  hideSwitch: boolean;
  /** Show editor */
  isEditing: boolean;
  /** Show loading */
  isEditingInFlight: boolean;
  /** Call to cancel edit */
  onCancelEdit: () => void;
  /** Call to start edit */
  onEdit: () => void;
  /** Call when editor throws an error */
  onErrorEdit: (error: Error) => void;
  /** Call to save current edit */
  onSaveEdit: (b: PaymentSourceInput) => void;
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceType) => void;
  /** User's currently saved bank details */
  savedSource: BankPaymentSource | null;
  /** Supported payment methods (bank, card etc.) */
  supportedMethods: Array<SupportedPaymentMethod>;
  /** The array of payment methods selectable from the "Switch Payment Method" dropdown */
  switchMethodOptions: SupportedPaymentMethod[];
  /** The selected currency */
  selectedCurrency: Currency;
  /** Is there an overdue invoice? */
  overdueInvoice?: OverdueInvoice;
  /** Show payment changed successfully message */
  showChangeSuccessMessage: boolean;
  /** Call to hide change success message */
  onHideSuccessMessage: () => void;
};

const renderLabel = (props: Props) =>
  props.savedSource && !props.isEditing ? (
    <PaymentInfo paymentSource={props.savedSource} />
  ) : (
    <Text tag="h3" type="paragraph1ShortSemibold">
      Add a bank (US only)
    </Text>
  );

const PaymentMethodBank: React.FC<Props> = props => {
  // @ts-ignore - PaymentDetailsBank and what is being `get`'d here is not compatible
  const bankDetails: PaymentDetailsBank = get(props, [
    "savedSource",
    "details",
  ]);

  const bankMethod = props.supportedMethods.find(
    m => m.method === PaymentSourceType.bank
  );

  return (
    <PaymentMethod
      isEditing={props.isEditing}
      renderAside={() => <Aside {...props} />}
      renderLabel={partial(renderLabel, props)}
      overdueInvoice={props.overdueInvoice}
      showChangeSuccessMessage={props.showChangeSuccessMessage}
      onHideSuccessMessage={props.onHideSuccessMessage}
    >
      <BankAccountForm
        braintreeAPIKey={props?.apiKeys?.braintree ?? ""}
        error={props.error}
        isEditingInFlight={props.isEditingInFlight}
        onCancelEdit={props.onCancelEdit}
        onError={props.onErrorEdit}
        onSave={props.onSaveEdit}
        paymentProcessor={get(
          bankMethod,
          "paymentProcessor",
          PaymentProcessor.braintree
        )}
        savedAccount={bankDetails}
        selectedCurrency={props.selectedCurrency}
        supportedSubmethods={props.supportedMethods
          .filter(m => m.method === PaymentSourceType.bank)
          .map(m => m.submethod)}
      />
    </PaymentMethod>
  );
};

export default PaymentMethodBank;
